import { useState } from "react";
import { Button } from "@/shared/ui/button";
import type { Severity } from "../types";
import { SeverityPicker } from "./SeverityPicker";

/**
 * Variant B (default, blind): our answer is hidden. The client classifies from
 * scratch — is there a deficiency, and if so how severe.
 */
export function ScratchControls({
  onSubmit,
  busy,
}: {
  onSubmit: (d: {
    clientIsDeficiency?: boolean;
    clientSeverity?: Severity;
    clientCantAssess?: boolean;
    reason?: string;
  }) => void;
  busy?: boolean;
}) {
  const [mode, setMode] = useState<"idle" | "severity">("idle");

  if (mode === "severity") {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">How severe is it?</p>
        <SeverityPicker disabled={busy} onPick={(s) => onSubmit({ clientIsDeficiency: true, clientSeverity: s })} />
        <Button variant="ghost" size="sm" onClick={() => setMode("idle")}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => setMode("severity")}
        className="border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-700"
      >
        Yes — there's a deficiency
      </Button>
      <Button variant="secondary" disabled={busy} onClick={() => onSubmit({ clientIsDeficiency: false })}>
        No deficiency here
      </Button>
      <Button
        variant="ghost"
        disabled={busy}
        onClick={() => onSubmit({ clientCantAssess: true })}
        className="text-muted-foreground"
      >
        Can't tell — poor image
      </Button>
      {/* Miscategorized nameplate/tag photos slip past the category filter — a
          distinct skip keeps them out of agree/disagree AND records the
          photo-category correction signal (reason prefix, exported). */}
      <Button
        variant="ghost"
        disabled={busy}
        onClick={() => onSubmit({ clientCantAssess: true, reason: "[tag/label photo — not judgeable]" })}
        className="text-muted-foreground"
      >
        It's an ID tag/label — skip
      </Button>
    </div>
  );
}
